import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { GoogleMap, LoadScript, Polygon, Marker, OverlayView } from '@react-google-maps/api';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import * as turf from '@turf/turf'; // 👈 Add this at the top
import { Menu } from "lucide-react"; // hamburger icon


// You'll need to replace this with your actual Google Maps API key
const GOOGLE_MAPS_API_KEY = "AIzaSyDC9amqdqZOGs2Jyx9hw65fUPSALOf5U3E";
import RankLegend from './RankLegend';
import FAQSection from './FAQSection';
import Footer from './Footer'; 
import SchoolListView from './SchoolListView';
import Header from './Header'; // Add this import


import ReactGA from 'react-ga4';
ReactGA.initialize('G-Z3ST6N1TTP');



// Constants and utility functions (remain the same)
const mapContainerStyle = {
  width: '100%',
  height: '100%',
  minHeight: '70%',

};

const center = {
  lat: -37.8136, // Melbourne's coordinates
  lng: 144.9631
};

// Memoized color function based on school rank
const getColorByRank = (rank) => {
  // Handle non-numeric ranks
  const parsedRank = parseInt(rank);
  if (isNaN(parsedRank)) return "#999"; // default gray
  
  // Define colors and their rank breakpoints
  const colorStops = [
    { rank: 1, color: { r: 26, g: 152, b: 80 } },    // #1a9850 (dark green) for top rank
    { rank: 50, color: { r: 102, g: 189, b: 99 } },  // #66bd63 (medium green)
    { rank: 100, color: { r: 166, g: 217, b: 106 } }, // #a6d96a (light green)
    { rank: 200, color: { r: 253, g: 174, b: 97 } }, // #fdae61 (yellow-orange)
    { rank: 300, color: { r: 244, g: 109, b: 67 } }, // #f46d43 (orange-red)
    { rank: 400, color: { r: 215, g: 48, b: 39 } }   // #d73027 (red) for lower ranks
  ];
  
  // Clamp rank to the range we have colors for
  const clampedRank = Math.max(1, Math.min(parsedRank, 500));
  
  // Find the two color stops between which this rank falls
  let lowerStop = colorStops[0];
  let upperStop = colorStops[colorStops.length - 1];
  
  for (let i = 0; i < colorStops.length - 1; i++) {
    if (clampedRank >= colorStops[i].rank && clampedRank <= colorStops[i + 1].rank) {
      lowerStop = colorStops[i];
      upperStop = colorStops[i + 1];
      break;
    }
  }
  
  // Calculate how far between the two stops this rank is (0-1)
  const rankRange = upperStop.rank - lowerStop.rank;
  const rankPosition = (clampedRank - lowerStop.rank) / rankRange;
  
  // Interpolate between the colors
  const r = Math.round(lowerStop.color.r + rankPosition * (upperStop.color.r - lowerStop.color.r));
  const g = Math.round(lowerStop.color.g + rankPosition * (upperStop.color.g - lowerStop.color.g));
  const b = Math.round(lowerStop.color.b + rankPosition * (upperStop.color.b - lowerStop.color.b));
  
  // Convert to hex
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

// Transform GeoJSON coordinates to Google Maps LatLng - Memoizable function
const getPolygonPaths = (coordinates) => {
  try {
    if (!Array.isArray(coordinates) || coordinates.length === 0) return [];
    // Handle MultiPolygon: [[[ [lng, lat], ... ]]]
    if (Array.isArray(coordinates[0][0][0])) {
      // Flatten all polygons into one array of paths
      return coordinates.flatMap(polygon =>
        polygon[0].map(coord => ({
          lat: parseFloat(coord[1]),
          lng: parseFloat(coord[0])
        }))
      );
    }
    // Handle Polygon: [[ [lng, lat], ... ]]
    if (Array.isArray(coordinates[0])) {
      return coordinates[0].map(coord => ({
        lat: parseFloat(coord[1]),
        lng: parseFloat(coord[0])
      }));
    }
    return [];
  } catch (err) {
    console.error("Error converting coordinates:", err);
    return [];
  }
};

const formatPrice = (price) => {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0
  }).format(price);
};


// Define any Google Maps libraries you need
const libraries = ['places']; // Example: if you use Autocomplete

const DONATION_URL = "https://www.buymeacoffee.com/datafun";

const App = () => {
  // State to track if the Google Maps API has loaded
  const [isLoaded, setIsLoaded] = useState(false);
  const [showDonateModal, setShowDonateModal] = useState(false);

  // Function to be called when the LoadScript successfully loads the API
  const handleLoad = () => {
    setIsLoaded(true);
    console.log('Google Maps API loaded successfully in App.js');
  };

  // Function to handle errors during API loading
  const handleError = (error) => {
    console.error('Error loading Google Maps API in App.js:', error);
    // You might want to display an error message to the user here
  };

  // Show donation modal after 30 seconds on every load (no persistence)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const timer = setTimeout(() => {
      setShowDonateModal(true);
    }, 30000); // show after 30 seconds
    return () => clearTimeout(timer);
  }, []);

  const openDonateModal = useCallback(() => {
    setShowDonateModal(true);
  }, []);

  const closeDonateModal = () => setShowDonateModal(false);

  return (
    <Router>
      <LoadScript
        googleMapsApiKey={GOOGLE_MAPS_API_KEY}
        libraries={libraries}
        onLoad={handleLoad}
        onError={handleError}
      >
        <Routes>
          <Route path="/" element={<Navigate replace to="/victoria/secondary" />} />
          {/* Pass the isLoaded prop to MapView */}
          <Route path="/victoria/:schoolType" element={<MapView isLoaded={isLoaded} onSupportClick={openDonateModal} />} />
          <Route path="/schools/:schoolType" element={<SchoolListView onSupportClick={openDonateModal} />} />
          <Route path="/faq" element={<FAQView onSupportClick={openDonateModal} />} />
        </Routes>
        {showDonateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 relative">
              <button
                onClick={closeDonateModal}
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
                aria-label="Close donation prompt"
              >
                ✕
              </button>
              <h2 className="text-xl font-semibold mb-2">Support this project</h2>
              <p className="text-gray-700 mb-4">
                If you find this helpful, please consider supporting us with a small donation. Your support keeps the school zone data fresh and the map running.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <button
                  onClick={closeDonateModal}
                  className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium"
                >
                  Maybe later
                </button>
                <a
                  href={DONATION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 rounded bg-yellow-500 hover:bg-yellow-600 text-white font-semibold text-center"
                >
                  ☕ Donate
                </a>
              </div>
            </div>
          </div>
        )}
      </LoadScript>
    </Router>
  );
};

// FAQ View Component
const FAQView = ({ onSupportClick }) => {
  return (
    <div className="h-screen flex flex-col">
      <Header activeTab="faq" onSupportClick={onSupportClick} />

      
      <div className="flex-1 overflow-auto">
        <FAQSection />
      </div>
      
      <Footer />
    </div>
  );
};

// Map View Component
const MapView = ({ isLoaded, onSupportClick }) => { // Receive isLoaded as a prop
  const { schoolType } = useParams();
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);
  

  // Create a function to close the sidebar
  const closeSidebar = () => {
    if (!isLargeScreen && sidebarOpen) {
      setSidebarOpen(false);
    }
  };

  // Modify the map click handler to close the sidebar
  const handleMapClick = () => {
    closeSidebar();
  };


// Add a ref to the sidebar
const sidebarRef = useRef(null);

// Update useEffect to add a click event listener
  useEffect(() => {
    function handleClickOutside(event) {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target) && !isLargeScreen && sidebarOpen) {
        setSidebarOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sidebarRef, isLargeScreen, sidebarOpen]);

  const [primaryZones, setPrimaryZones] = useState(null);
  const [secondaryZones, setSecondaryZones] = useState(null);
  const [selectedSchoolType, setSelectedSchoolType] = useState("Secondary");
  
  // Update URL when school type changes
  useEffect(() => {
    navigate(`/victoria/${selectedSchoolType.toLowerCase()}`);
  }, [selectedSchoolType, navigate]);
  
  // Rest of your state variables
  const [priceType, setPriceType] = useState("house");
  const [priceRange, setPriceRange] = useState(5000000);
  const [maxRank, setMaxRank] = useState(542);
  const [localMaxRank, setLocalMaxRank] = useState(542);

  // Add state variables to store maximum ranks for each school type
  const [primaryMaxRank, setPrimaryMaxRank] = useState(0);
  const [secondaryMaxRank, setSecondaryMaxRank] = useState(0);

  const [filteredZones, setFilteredZones] = useState([]);
  const [mapInstance, setMapInstance] = useState(null);
  const [activeInfoWindow, setActiveInfoWindow] = useState(null);
  const [error, setError] = useState(null);
  
  const [showSuburbBoundaries, setShowSuburbBoundaries] = useState(false);
  const [showSchoolBoundaries, setShowSchoolBoundaries] = useState(true);
  const [vicSuburbs, setVicSuburbs] = useState({ features: [] });
  
  const [hoveredPolygonIndex, setHoveredPolygonIndex] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(11);
  
  const [infoMessage, setInfoMessage] = useState("Use the filters to find schools based on price and rank.");
  
  const [processedSuburbs, setProcessedSuburbs] = useState([]);
  
  // Update screen size state on resize
  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load GeoJSON data - with error handling and preprocessing
  useEffect(() => {
    async function loadData() {
      try {
        // Load suburbs
        const suburbResponse = await fetch('/enriched_vic_suburbs.geojson');
        console.log("Suburb response:", suburbResponse);
        const suburbData = await suburbResponse.json();
        const features = suburbData.features || [];
        setVicSuburbs({ type: "FeatureCollection", features });
        
        // Pre-process suburbs
        const processed = features.map(feature => {
          try {
            const coordinates = feature.geometry?.coordinates;
            if (!coordinates || !Array.isArray(coordinates)) return null;
            
            const paths = getPolygonPaths(coordinates);
            if (paths.length === 0) return null;
            
            // Pre-calculate centroid
            const centroid = turf.centroid(feature);
            const [lng, lat] = centroid.geometry.coordinates;
            
            return {
              feature,
              paths,
              centroid: { lat, lng }
            };
          } catch (err) {
            console.error("Error processing suburb:", feature.properties?.scc_name, err);
            return null;
          }
        }).filter(Boolean); // Remove nulls
        
        setProcessedSuburbs(processed);
  
        // Load Primary Schools GeoJSON
        const primaryResponse = await fetch('/enriched_primary_school_zones.geojson');
        console.log("Primary response:", primaryResponse);
        const primaryData = await primaryResponse.json();
        
        // Find maximum rank in primary schools
        let primaryMaxRank = 0;
        primaryData.features.forEach(feature => {
          const rank = parseInt(feature.properties.Rank) || 0;
          if (rank > primaryMaxRank) primaryMaxRank = rank;
        });

        setPrimaryMaxRank(primaryMaxRank); // Store primary max rank

        // Pre-process primary schools
        const processedPrimary = {
          ...primaryData,
          features: primaryData.features.map(feature => {
            try {
              // Pre-calculate paths and centroids
              const paths = getPolygonPaths(feature.geometry.coordinates);
              const centroid = turf.centroid(feature);
              const [lng, lat] = centroid.geometry.coordinates;
              
              return {
                ...feature,
                _processed: {
                  paths,
                  centroid: { lat, lng },
                  color: getColorByRank(feature.properties.Rank)
                }
              };
            } catch (err) {
              console.error("Error processing primary school:", feature.properties?.School_Name, err);
              return feature;
            }
          })
        };
        setPrimaryZones(processedPrimary);
  
        // Load Secondary Schools GeoJSON
        const secondaryResponse = await fetch('/enriched_secondary_school_zones.geojson');
        console.log("Secondary response:", secondaryResponse);
        const secondaryData = await secondaryResponse.json();
        
        // Find maximum rank in secondary schools
        let secondaryMaxRank = 0;
        secondaryData.features.forEach(feature => {
          const rank = parseInt(feature.properties.Rank) || 0;
          if (rank > secondaryMaxRank) secondaryMaxRank = rank;
        });

        setSecondaryMaxRank(secondaryMaxRank); // Store secondary max rank

        // Pre-process secondary schools
        const processedSecondary = {
          ...secondaryData,
          features: secondaryData.features.map(feature => {
            try {
              // Pre-calculate paths and centroids
              const paths = getPolygonPaths(feature.geometry.coordinates);
              const centroid = turf.centroid(feature);
              const [lng, lat] = centroid.geometry.coordinates;
              
              return {
                ...feature,
                _processed: {
                  paths,
                  centroid: { lat, lng },
                  color: getColorByRank(feature.properties.Rank)
                }
              };
            } catch (err) {
              console.error("Error processing secondary school:", feature.properties?.School_Name, err);
              return feature;
            }
          })
        };
        setSecondaryZones(processedSecondary);
      
        // Set the initial maxRank based on the currently selected school type
        setMaxRank(selectedSchoolType === "Primary" ? primaryMaxRank : secondaryMaxRank);
    
        // Set the maximum rank as the default value
        const overallMaxRank = Math.max(primaryMaxRank, secondaryMaxRank);
        setMaxRank(overallMaxRank);

        setInfoMessage(`Loaded ${primaryData.features?.length || 0} primary and ${secondaryData.features?.length || 0} secondary school zones.`);
      } catch (error) {
        console.error("Error loading data:", error);
        setError(`Failed to load data: ${error.message}`);
      }
    }
    loadData();
  }, []);
  
  // Add an effect to update maxRank when school type changes
  useEffect(() => {
    if (selectedSchoolType === "Primary" && primaryMaxRank > 0) {
      setMaxRank(primaryMaxRank);
      setLocalMaxRank(primaryMaxRank); // Update localMaxRank too if using it
    } else if (selectedSchoolType === "Secondary" && secondaryMaxRank > 0) {
      setMaxRank(secondaryMaxRank);
      setLocalMaxRank(secondaryMaxRank); // Update localMaxRank too if using it
    }
  }, [selectedSchoolType, primaryMaxRank, secondaryMaxRank]);

  // Filter zones based on price, school type, and rank - now with useMemo
  useEffect(() => {
    const filterZones = () => {
      const zonesToFilter = 
        selectedSchoolType === "Primary" ? primaryZones :
        selectedSchoolType === "Secondary" ? secondaryZones :
        null;
      
      if (!zonesToFilter) return [];
      
      const priceField = priceType === "house" ? "median_house_price" : "median_unit_price";
      
      const filtered = zonesToFilter.features.filter(feature => {
        const price = feature.properties[priceField] || 0;
        const rank = parseInt(feature.properties.Rank) || 0;
        return price*0.9 <= priceRange && rank <= maxRank;
      });
      
      setFilteredZones(filtered);
      setInfoMessage(`Showing ${filtered.length} ${selectedSchoolType.toLowerCase()} school zones matching your criteria.`);
    };
    
    filterZones();
  }, [primaryZones, secondaryZones, priceRange, priceType, maxRank, selectedSchoolType]);

  
  // Map loading callback (same as before)
  const onMapLoad = useCallback((map) => {
    console.log("Google Map loaded successfully within MapView and is ready to use.");
    setMapInstance(map);
    setZoomLevel(map.getZoom());
  }, []);
  
  // Handle polygon click with useCallback to prevent recreation on each render
  const handlePolygonClick = useCallback((feature) => (event) => {
    // Stop event propagation to prevent suburb polygon from capturing the click
    if (event.domEvent) {
      event.domEvent.stopPropagation();
    }
    if (activeInfoWindow) {
      activeInfoWindow.close();
    }
  
    const name = feature.properties.School_Name || "Unknown School";
    const type = selectedSchoolType === "Primary" ? "Primary" : "Secondary";
    const suburb = feature.properties.Suburb || "Unknown Suburb";
    const housePrice = formatPrice(feature.properties.median_house_price || 0);
    const unitPrice = formatPrice(feature.properties.median_unit_price || 0);
    const score = feature.properties.Score || "N/A";
    const rank = feature.properties.Rank || "N/A";
    
    // Added performance indicators with color coding
    const rankClass = rank <= 50 ? "text-green-600 font-bold" : 
                     rank <= 150 ? "text-blue-600 font-bold" : 
                     "text-gray-700";
    
    const contentString = `
      <div style="padding: 12px; max-width: 320px; font-family: Arial, sans-serif;">
        <h3 style="font-size: 18px; font-weight: bold; margin-bottom: 10px; color: #1a4f8a;">${name}</h3>
        
        <div style="background-color: #f3f4f6; border-radius: 6px; padding: 10px; margin-bottom: 12px;">
          <p style="margin: 4px 0; display: flex; justify-content: space-between;">
            <span style="font-weight: bold;">School Type</span> 
            <span>${type} School</span>
          </p>
        <p style="margin: 4px 0; display: flex; justify-content: space-between;">
        <span style="font-weight: bold;">State ${selectedSchoolType === "Primary" ? "NAPLAN" : "VCE"} Rank:</span> 
        <span class="${rankClass}">${rank === "0" ? "Unknown" : `${rank}`}</span>
        </p>
        <p style="margin: 4px 0; display: flex; justify-content: space-between;">
        <span style="font-weight: bold;">Median ${selectedSchoolType === "Primary" ? "NAPLAN" : "VCE"} Score:</span> 
        <span class="${rankClass}">${score === "0" ? "Unknown" : score}</span>
        </p>
          <p style="margin: 4px 0; display: flex; justify-content: space-between;">
            <span style="font-weight: bold;">Main Suburb:</span> 
            <span>${suburb}</span>
          </p>
        </div>
        
        <div style="border-top: 1px solid #e5e7eb; padding-top: 10px;">
          <p style="font-weight: bold; margin: 6px 0; color: #4b5563;">Property Prices in Catchment Area:</p>
          <div style="display: flex; justify-content: space-between; margin-top: 8px;">
            <div style="text-align: center; width: 48%;">
              <p style="font-size: 14px; color: #6b7280; margin: 0;">House</p>
              <p style="font-size: 16px; font-weight: bold; color: #1a4f8a; margin: 4px 0;">
                ${housePrice === "$0" ? "Unknown" : housePrice}
              </p>
            </div>
            <div style="text-align: center; width: 48%;">
              <p style="font-size: 14px; color: #6b7280; margin: 0;">Unit</p>
              <p style="font-size: 16px; font-weight: bold; color: #1a4f8a; margin: 4px 0;">
                ${unitPrice === "$0" ? "Unknown" : unitPrice}
              </p>
            </div>
          </div>
        </div>
        
        <div style="margin-top: 12px; font-size: 12px; color: #6b7280; text-align: center;">
        </div>
      </div>
    `;
    
    const infoWindow = new window.google.maps.InfoWindow({
      content: contentString,
      position: event.latLng,
      maxWidth: 340
    });
    
    infoWindow.open(mapInstance);
    setActiveInfoWindow(infoWindow);
    setInfoMessage(`Selected: ${name} - Rank: ${rank}`);
  }, [activeInfoWindow, mapInstance]);
  
  // Handle suburb click - with useCallback
  const handleSuburbClick = useCallback((feature) => (event) => {
    if (activeInfoWindow) {
      activeInfoWindow.close();
    }
    
    const suburbName = feature.properties.scc_name || "Unknown";
    const housePrice = formatPrice(feature.properties.median_house_price || 0);
    const unitPrice = formatPrice(feature.properties.median_unit_price || 0);
    
    const contentString = `
      <div style="padding: 8px; max-width: 300px;">
      <h3 style="font-size: 16px; font-weight: bold; margin-bottom: 8px;">${suburbName}</h3>
      <p style="margin: 4px 0;"><strong>Median House Price:</strong> ${housePrice === "$0" ? "Unknown" : housePrice}</p>
      <p style="margin: 4px 0;"><strong>Median Unit Price:</strong> ${unitPrice === "$0" ? "Unknown" : unitPrice}</p>
      </div>
    `;
    
    const infoWindow = new window.google.maps.InfoWindow({
      content: contentString,
      position: event.latLng,
      maxWidth: 320
    });
    
    infoWindow.open(mapInstance);
    setActiveInfoWindow(infoWindow);
    setInfoMessage(`Selected: ${suburbName} - House: ${housePrice}, Unit: ${unitPrice}`);
  }, [activeInfoWindow, mapInstance]);

  // Memoize the suburbs rendering to prevent recalculation
  const renderedSuburbs = useMemo(() => {
    if (!showSuburbBoundaries || !processedSuburbs.length) return null;
    
    return processedSuburbs.map((item, i) => {
      const { feature, paths, centroid } = item;
      const suburbName = feature.properties.scc_name || "Unknown";
      const median = priceType === "house" 
        ? feature.properties.median_house_price || 0
        : feature.properties.median_unit_price || 0;
      
      return (
        <React.Fragment key={`suburb-${i}`}>
          <Polygon
            paths={paths}
            options={{
              fillColor: "#888",
              fillOpacity: 0.1,
              strokeColor: "#444",
              strokeWeight: 1,
              clickable: true,
              zIndex: 1
            }}
            onClick={handleSuburbClick(feature)}
          />
          {median > 0 && zoomLevel >= 11 && (
            <Marker
              position={centroid}
              label={{
                text: `$${(median / 1000).toFixed(0)}k`,
                color: "black",
                fontSize: "12px",
                fontWeight: "bold"
              }}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                fillOpacity: 0,
                strokeOpacity: 0,
                scale: 1,
              }}
              zIndex={2}
            />
          )}
        </React.Fragment>
      );
    });
  }, [showSuburbBoundaries, processedSuburbs, priceType, zoomLevel, handleSuburbClick]);


  
const renderedSchoolZones = useMemo(() => {
  if (!showSchoolBoundaries || !filteredZones.length) return null;
  
  return filteredZones.map((zone, index) => {
    // Use pre-processed paths and centroids if available
    const paths = zone._processed?.paths || getPolygonPaths(zone.geometry.coordinates);
    const centroid = zone._processed?.centroid || (() => {
      const cent = turf.centroid(zone);
      return { lat: cent.geometry.coordinates[1], lng: cent.geometry.coordinates[0] };
    })();
    
    const color = zone._processed?.color || getColorByRank(zone.properties.Rank);

    // More detailed school building SVG path
    const schoolIconPath = "M3,21H10V15H14V21H21V7L12,3L3,7V21M6,9H9V11H6V9M6,13H9V15H6V13M6,17H9V19H6V17M15,9H18V11H15V9M15,13H18V15H15V13M15,17H18V19H15V17Z";

    return (
      <React.Fragment key={`school-zone-${index}`}>
        <Polygon
          paths={paths}
          options={{
            fillColor: color,
            fillOpacity: 0.6,
            strokeColor: 'steel blue',
            strokeWeight: hoveredPolygonIndex === index ? 3 : 1,
            clickable: true,
            zIndex: 3
          }}
          onClick={handlePolygonClick(zone)}
        />

        {/* Detailed school building icon at centroid with click handler */}
        <Marker
          position={centroid}
          icon={{
            path: schoolIconPath,
            fillColor: "sky blue", // Google blue color
            fillOpacity: 1,
            strokeColor: "#FFFFFF", 
            strokeWeight: 1,
            scale: 0.5,
            anchor: new window.google.maps.Point(12, 12),
          }}
          onClick={handlePolygonClick(zone)}
          zIndex={5}
        />
      </React.Fragment>
    );
  });
}, [showSchoolBoundaries, filteredZones, hoveredPolygonIndex, handlePolygonClick]);

  // Sidebar Content Component
  const SidebarContent = ({ maxRank }) => {
    // Local state for input values
    const [localPriceRange, setLocalPriceRange] = useState(priceRange);
    const [localMaxRank, setLocalMaxRank] = useState(maxRank);
   
    // Update localMaxRank when maxRank changes from data loading
    useEffect(() => {
      setLocalMaxRank(maxRank);
    }, [maxRank]);

    // Function to apply all filters at once
    const applyAllFilters = () => {
      setPriceRange(localPriceRange);
      setMaxRank(localMaxRank);
      closeSidebar();
    };
    
    const formatNumberWithCommas = (number) => {
      return new Intl.NumberFormat('en-AU').format(number);
    };

    const parseNumberWithoutCommas = (value) => {
      return value.replace(/,/g, '');
    };

    return (
      <>
        <div className="p-4">
          <p className="font-bold mb-2">Use filters to match top school zones with budget</p>
          
          {/* Price Type Selection */}
                <div className="mb-4">
                <h3 className="font-medium mb-2">Property Type</h3>
                <fieldset>
                  <legend className="sr-only">Select property type for price comparison</legend>
                  <div className="flex space-x-4">
                  <label className="inline-flex items-center">
                    <input
                    type="radio"
                    name="priceType"
                    value="house"
                    checked={priceType === "house"}
                    onChange={() => setPriceType("house")}
                    className="form-radio h-4 w-4 text-blue-600"
                    />
                    <span className="ml-2 text-sm text-gray-700">House</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                    type="radio"
                    name="priceType"
                    value="unit"
                    checked={priceType === "unit"}
                    onChange={() => setPriceType("unit")}
                    className="form-radio h-4 w-4 text-blue-600"
                    />
                    <span className="ml-2 text-sm text-gray-700">Unit/Apartment</span>
                  </label>
                  </div>
                </fieldset>
                </div>
                
                {/* Price Range - with input field */}
                <div className="mb-4">
                <h3 className="font-medium mb-2">Maximum {priceType === "house" ? "House" : "Unit"} Budget</h3>
                <div className="flex items-center gap-2">
                  <label htmlFor="price-range" className="sr-only">Maximum property price</label>
                  <input
                  id="price-range"
                  type="text"
                  min="0"
                  max="5000000"
                  step="50000"
                  value={localPriceRange === null ? "" : formatNumberWithCommas(localPriceRange)}
                  onChange={(e) => {
                    const rawValue = parseNumberWithoutCommas(e.target.value);
                    // Basic validation to ensure only numbers and commas are being typed
                    if (/^[0-9,]*$/.test(e.target.value)) {
                    setLocalPriceRange(rawValue === "" ? null : Number(rawValue));
                    }
                  }}
                  onBlur={() => {
                    if (localPriceRange === null) {
                    setLocalPriceRange(500000); // reset to default if left blank
                    } else {
                    setLocalPriceRange(Number(parseNumberWithoutCommas(formatNumberWithCommas(localPriceRange))));
                    }
                  }}
                  className="w-full border rounded p-2"
                  placeholder="Enter maximum price"
                  aria-describedby="price-range-hint"
                  />
                </div>
                <p id="price-range-hint" className="text-xs text-gray-400 mt-1">Average price of suburbs in the school catchment area (with 10% buffer)</p>
                </div>
                
                {/* School Rank Filter - with input field */}
                <div className="mb-4">
                <h3 className="font-medium mb-2">Filter by Rank in Victoria</h3>
                <div className="flex items-center gap-2">
                  <label htmlFor="max-rank" className="sr-only">Maximum school rank</label>
                  <input
                    id="max-rank"
                    type="text"
                    min="1"
                    max={maxRank}  // Use dynamic maxRank instead of hardcoded 542
                    step="1"
                    value={localMaxRank === null ? "" : localMaxRank}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      // Basic validation to ensure only numbers are being typed
                      if (/^[0-9]*$/.test(rawValue)) {
                        setLocalMaxRank(rawValue === "" ? null : Number(rawValue));
                      }
                    }}
                    onBlur={() => {
                      if (localMaxRank === null) {
                        setLocalMaxRank(maxRank); // Use dynamic maxRank instead of hardcoded value
                      }
                    }}
                    className="w-full border rounded p-2"
                    placeholder="Enter maximum rank"
                    aria-describedby="rank-hint"
                  />
                </div>
                <p id="rank-hint" className="text-xs text-gray-400 mt-1">Show non-selective public schools ranked from 1 to your selected number.</p>
                </div>
          <div className="mb-4">
            <h3 className="font-medium mb-2">School Type</h3>
            <fieldset>
              <legend className="sr-only">Select school level</legend>
              <div className="flex flex-col gap-1">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="schoolType"
                    value="Primary"
                    checked={selectedSchoolType === "Primary"}
                    onChange={() => setSelectedSchoolType("Primary")}
                    className="mr-2"
                    
                  /> 
                  <span className="text-sm text-gray-700">Primary Schools</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="schoolType"
                    value="Secondary"
                    checked={selectedSchoolType === "Secondary"}
                    onChange={() => setSelectedSchoolType("Secondary")}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Secondary Schools</span>
                </label>
              </div>
            </fieldset>
          </div>

          {/* Layer Toggles */}
          <div className="mt-4 mb-6">
            <h3 className="font-medium mb-2">Map Visualization</h3>
            <div className="flex flex-col space-y-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="showSchools"
                  checked={showSchoolBoundaries}
                  onChange={() => setShowSchoolBoundaries(!showSchoolBoundaries)}
                  className="mr-2"
                />
                <label htmlFor="showSchools" className="text-sm text-gray-700">Show School Catchment Areas</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="showSuburbs"
                  checked={showSuburbBoundaries}
                  onChange={() => setShowSuburbBoundaries(!showSuburbBoundaries)}
                  className="mr-2"
                />
                <label htmlFor="showSuburbs" className="text-sm text-gray-700">Show Suburb Property Prices</label>
              </div>
            </div>
          </div>

          {/* Apply Filter Button */}
          <button
            onClick={applyAllFilters}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            aria-label="Apply all filter settings"
          >
            Find School Zones
          </button>
          
          <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <h3 className="font-medium text-blue-800 mb-2">Why School Zones Matter</h3>
            <p className="text-sm text-gray-600 mb-2">
              Living within a school's designated catchment area guarantees your child's enrollment eligibility. 
              Top-performing schools often correlate with higher property values.
            </p>
            <p className="text-sm text-gray-600">
              This tool helps parents and property investors find the sweet spot between school performance and affordability.
            </p>
          </div>
        </div>
      </>
    );
  };

  const pageTitle = "Melbourne School Zone Property Finder | Find Top Government Schools by Property Price";
  const pageDescription = "Find top-performing Melbourne, Victoria government school zones by property prices.";

  return (
    <div className="h-screen flex flex-col relative">
      <Helmet>
        {/* Basic SEO */}
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content="Melbourne school zones, school catchment areas, property prices, education rankings, NAPLAN, VCE, Victoria schools, property investment, school finder" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://goodschoolsbyhouseprice.com" />
        
        {/* Language */}
        <html lang="en-AU" />
        
        {/* Favicon tags */}
        <link rel="icon" href="/favicon.svg" type="image/svg+xml" />
        <link rel="alternate icon" href="/favicon.ico" type="image/x-icon" />
        <link rel="apple-touch-icon" href="/apple-touch-icon.png" sizes="180x180" />
        <link rel="manifest" href="/site.webmanifest" />
        <meta name="theme-color" content="#1a4f8a" />
        
        {/* Viewport and Mobile Optimization */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="format-detection" content="telephone=no" />
        
        {/* Structured Data for Google */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Melbourne Best School Zones by Property Price",
            "description": "${pageDescription}",
            "url": "https://goodschoolsbyhouseprice.com",
            "applicationCategory": "Education",
            "operatingSystem": "Web",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "AUD"
            },
            "audience": {
              "@type": "Audience",
              "audienceType": ["Parents", "Property Investors", "Education Planners"]
            }
          }
        `}</script>
      </Helmet>
      
      <Header
        activeTab="map"
        selectedSchoolType="secondary"
        isLargeScreen={isLargeScreen}
        setSidebarOpen={setSidebarOpen}
        sidebarOpen={sidebarOpen}
        onSupportClick={onSupportClick}
      />

      <div className="flex flex-1 relative overflow-auto">
        {/* Fixed sidebar for large screens */}
        {isLargeScreen && (
          <aside className="w-80 bg-white shadow-lg z-10 overflow-y-auto">
            <SidebarContent maxRank={maxRank}/>
          </aside>
        )}
        
        {/* Mobile sidebar (slide in/out) */}
        {!isLargeScreen && sidebarOpen && (
           <aside ref={sidebarRef} className="absolute top-0 left-0 w-80 h-full bg-white shadow-lg z-20 overflow-y-auto">
             <div className="flex justify-between items-center p-4 bg-blue-600 text-white">
              <h3 className="font-bold">Filters</h3>
              <button 
                onClick={() => setSidebarOpen(false)}
                className="text-white"
              >
                ×
              </button>
            </div>
            <SidebarContent maxRank={maxRank}/>
          </aside>
        )}
        
        {/* Full-width map container */}
        <div className="w-full h-full relative flex flex-col">
          <div className="flex-grow" onClick={handleMapClick}>
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={center}
              zoom={11}
              onLoad={onMapLoad}
              onClick={handleMapClick}
              onZoomChanged={() => {
                if (mapInstance) {
                  setZoomLevel(mapInstance.getZoom());
                }
              }}
              options={{
                zoomControl: true,
                scrollwheel: true,
                gestureHandling: 'greedy',
                streetViewControl: false,
                mapTypeControl: false,
                draggable: true,
                
              }}
            >
              {/* ... Map layers (renderedSuburbs, renderedSchoolZones) */}
              {renderedSuburbs}
              {renderedSchoolZones}
            </GoogleMap>
          </div>
          
          {/* Info area and rank legend - improved layout */}
          <div className="bg-white border-t p-3 text-sm flex flex-wrap justify-between items-center">
            <div className="w-full md:w-1/2 mb-2 md:mb-0">
              <p className="font-medium">{infoMessage}</p>
              {error && <p className="text-red-600 mt-1">Error: {error}</p>}
            </div>
            
            <div className="w-full md:w-1/2 flex flex-wrap justify-end items-center gap-4">
              <RankLegend />
            </div>
          </div>

          <Footer />
        </div>
      </div>
    </div>
  );
};

export default App;